import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Mic, Coins, Wallet, Heart } from 'lucide-react';
import { useAccount } from 'wagmi';
import { testimonyEvents } from '../utils/analytics';
import './Hero.css';

const Hero = () => {
  const { isConnected } = useAccount();
  const [activeTooltip, setActiveTooltip] = React.useState(null);
  
  const scrollToForm = () => {
    testimonyEvents.scrollToForm();
    document.getElementById('submit')?.scrollIntoView({ behavior: 'smooth' });
  };

  const tooltipTexts = {
    guilt: "Small slip-up or life's biggest regret? Shame or guilt — share it, turn it into good, and let it go forever.",
    charity: "Pick 3 causes you care about most. Every confession triggers donations — your story makes a difference.",
    token: "Each confession becomes its own blockchain token — viewable, tradable, and forever transparent on Zora.",
    wallet: "Unlock others' secrets and fill your wallet with emotions and stories — while fueling real-world change."
  };

  return (
    <section className={`hero ${isConnected ? 'wallet-connected' : ''}`}>
      <div className="hero-background">
        <div className="hero-gradient"></div>
        <div className="hero-particles">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: [0, 1, 0], 
                scale: [0, 1, 0],
                x: Math.random() * 100 - 50,
                y: Math.random() * 100 - 50
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </div>
      
      <div className="container">
        <div className="hero-content">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="hero-title">
              Share Your{' '}
              <motion.span 
                className="gradient-text"
                initial={{ backgroundPosition: '0% 50%' }}
                animate={{ backgroundPosition: '100% 50%' }}
                transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
              >
                Confession
              </motion.span>
            </h1>
            
            <p className="hero-subtitle">
              Turn your confessions into crypto that does good. Each story becomes a token — powering change and fueling charities worldwide.
            </p>
            
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">9</span>
                <span className="stat-label">Charities Supported</span>
              </div>
              <div className="stat">
                <span className="stat-number">∞</span>
                <span className="stat-label">Stories Shared</span>
              </div>
              <div className="stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">Transparent</span>
              </div>
            </div>
            
            <div className="hero-actions">
              <motion.button
                className="btn btn-primary btn-large pulse"
                onClick={scrollToForm}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Share Your Story
              </motion.button>
              
              <motion.button
                className="btn btn-secondary btn-large"
                onClick={() => document.getElementById('confessions')?.scrollIntoView({ behavior: 'smooth' })}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore Gallery
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="hero-flow-container">
              {/* Central Box */}
              <motion.div 
                className="hero-central-box"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <img src="/doboz_transzparens.png" alt="GuiltBox" />
              </motion.div>

              {/* Guilt (top left) */}
              <motion.div 
                className="hero-flow-element hero-mic-element"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                onMouseEnter={() => setActiveTooltip('guilt')}
                onMouseLeave={() => setActiveTooltip(null)}
                onClick={() => setActiveTooltip(activeTooltip === 'guilt' ? null : 'guilt')}
              >
                <div className="hero-icon-wrapper">
                  <Mic size={29} />
                </div>
                <span className="hero-element-label">Guilt</span>
                {activeTooltip === 'guilt' && (
                  <motion.div
                    className="hero-tooltip"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    {tooltipTexts.guilt}
                  </motion.div>
                )}
              </motion.div>

              {/* Charity (bottom right) */}
              <motion.div 
                className="hero-flow-element hero-good-element"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                onMouseEnter={() => setActiveTooltip('charity')}
                onMouseLeave={() => setActiveTooltip(null)}
                onClick={() => setActiveTooltip(activeTooltip === 'charity' ? null : 'charity')}
              >
                <div className="hero-icon-wrapper">
                  <Heart size={24} />
                </div>
                <span className="hero-element-label">Charity</span>
                {activeTooltip === 'charity' && (
                  <motion.div
                    className="hero-tooltip"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    {tooltipTexts.charity}
                  </motion.div>
                )}
              </motion.div>

              {/* Token (bottom left) */}
              <motion.div 
                className="hero-flow-element hero-token-element"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1.1 }}
                onMouseEnter={() => setActiveTooltip('token')}
                onMouseLeave={() => setActiveTooltip(null)}
                onClick={() => setActiveTooltip(activeTooltip === 'token' ? null : 'token')}
              >
                <div className="hero-icon-wrapper">
                  <Coins size={24} />
                </div>
                <span className="hero-element-label">Token</span>
                {activeTooltip === 'token' && (
                  <motion.div
                    className="hero-tooltip"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    {tooltipTexts.token}
                  </motion.div>
                )}
              </motion.div>

              {/* Wallet (top right) */}
              <motion.div 
                className="hero-flow-element hero-wallet-element"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1.3 }}
                onMouseEnter={() => setActiveTooltip('wallet')}
                onMouseLeave={() => setActiveTooltip(null)}
                onClick={() => setActiveTooltip(activeTooltip === 'wallet' ? null : 'wallet')}
              >
                <div className="hero-icon-wrapper">
                  <Wallet size={24} />
                </div>
                <span className="hero-element-label">Wallet</span>
                {activeTooltip === 'wallet' && (
                  <motion.div
                    className="hero-tooltip"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    {tooltipTexts.wallet}
                  </motion.div>
                )}
              </motion.div>

              {/* Flow Lines */}
              <svg className="flow-lines" viewBox="0 0 480 360">
                {/* Guilt to Box (top left corner to center) */}
                <motion.path
                  d="M100 100 L240 180"
                  stroke="rgba(255, 216, 155, 0.8)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 1.5 }}
                />
                
                {/* Box to Charity (center to bottom right corner) */}
                <motion.path
                  d="M240 180 L380 280"
                  stroke="rgba(255, 216, 155, 0.8)"
                  strokeWidth="2"
                  fill="none"
                  markerEnd="url(#arrowhead)"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 1.7 }}
                />

                {/* Charity to Token (curved from bottom right to bottom left corner) */}
                <motion.path
                  d="M360 300 Q240 330 100 300"
                  stroke="rgba(255, 216, 155, 0.8)"
                  strokeWidth="2"
                  fill="none"
                  markerEnd="url(#arrowhead)"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 1.9 }}
                />

                {/* Token to Box (bottom left corner to center) */}
                <motion.path
                  d="M100 280 L240 180"
                  stroke="rgba(255, 216, 155, 0.8)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 2.1 }}
                />

                {/* Box to Wallet (center to top right corner) */}
                <motion.path
                  d="M240 180 L350 100"
                  stroke="rgba(255, 216, 155, 0.8)"
                  strokeWidth="2"
                  fill="none"
                  markerEnd="url(#arrowhead)"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 2.3 }}
                />

                {/* Arrow marker definition */}
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon
                      points="0 0, 10 3.5, 0 7"
                      fill="rgba(255, 216, 155, 0.8)"
                    />
                  </marker>
                </defs>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
      
      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <ChevronDown size={24} />
      </motion.div>
    </section>
  );
};

export default Hero;